import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import emojiRegex from 'emoji-regex';
import { ConfigConstants } from '../../../config/config-constants';
import { ConfigParticipantCopySupportByNotificationType } from '../../../interfaces/config.interface';
import { LoadStatus } from '../../load-status';
import { CONFIG_FEATURE_KEY, ConfigState } from '../config.state';

export const selectConfigFeature = createFeatureSelector<ConfigState>(CONFIG_FEATURE_KEY);
export const selectConfigEntries = createSelector(selectConfigFeature, (state) => state.configValues);

/** Raw load status of the slice. */
export const selectConfigStatus: MemoizedSelector<object, LoadStatus> = createSelector(
    selectConfigFeature,
    (state) => state.status,
);

/** Whether the config slice has been populated from the backend. */
export const selectConfigLoaded: MemoizedSelector<object, boolean> = createSelector(
    selectConfigFeature,
    (state) => state.status === LoadStatus.Loaded,
);

/**
 * Config entries are stored as plain strings; these factories build the selector that reads one key
 * and coerces it to the type callers expect, falling back to `defaultValue` when the key is absent.
 *
 * @param key
 * @param defaultValue
 */
export const makeBooleanConfigSelector = (key: string, defaultValue: boolean): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) =>
        entries[key] === undefined ? defaultValue : entries[key] === ConfigConstants.sapTrue,
    );

export const makeStringConfigSelector = (key: string, defaultValue: string): MemoizedSelector<object, string> =>
    createSelector(selectConfigEntries, (entries) => (entries[key] !== undefined ? entries[key] : defaultValue));

export const makeNumberConfigSelector = (key: string, defaultValue: number): MemoizedSelector<object, number> =>
    createSelector(selectConfigEntries, (entries) =>
        entries[key] !== undefined ? parseInt(entries[key], 10) : defaultValue,
    );

export const makeStringArrayConfigSelector = (
    key: string,
    requireNonEmpty = false,
    defaultValue: string[] = [],
): MemoizedSelector<object, string[]> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[key];
        const present = requireNonEmpty ? value !== undefined && value.length > 0 : value !== undefined;
        return present ? value.split(',') : defaultValue;
    });

export const selectMaterialErrorCodeCountMax = makeNumberConfigSelector(
    ConfigConstants.configKeyMaterialErrorCodeCountMax,
    Infinity,
);
export const selectCseEmailFax = makeStringConfigSelector(ConfigConstants.configKeyCseEmailFax, '');
export const selectShowRefreshXMLButton = makeBooleanConfigSelector(
    ConfigConstants.configKeyShowRefreshXMLButton,
    false,
);
export const selectDisplayCustomerContact = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayCustomerContact,
    false,
);
export const selectShowLanguageSelectionDropdown = makeBooleanConfigSelector(
    ConfigConstants.configKeyShowLanguageSelectionDropdown,
    false,
);
export const selectEscalationEmail = makeStringConfigSelector(ConfigConstants.configKeyEscalationEmail, '');
export const selectInstructionsUrl = makeStringConfigSelector(ConfigConstants.configKeyInstructionsUrl, '');
export const selectShowDocumentationLink = makeBooleanConfigSelector(
    ConfigConstants.configKeyShowDocumentationLink,
    false,
);
export const selectUserFrontId = makeStringConfigSelector(ConfigConstants.configKeyUserFrontId, '');
export const selectMaxInterimsCount = makeNumberConfigSelector(ConfigConstants.configKeyMaxInterimsCount, 0);
export const selectExpenseCurrency = makeStringConfigSelector(ConfigConstants.configKeyExpenseCurrency, '');
export const selectCseCountry = makeStringConfigSelector(ConfigConstants.configKeyCseCountry, '');
export const selectShowInfoToCCC = makeBooleanConfigSelector(ConfigConstants.configKeyShowInfoToCCC, false);
export const selectLegalCheckIEC62353Text = makeStringConfigSelector(ConfigConstants.configKeyLegalIEC62353Text, '');
export const selectLegalCheckIEC62353Code = makeStringConfigSelector(ConfigConstants.configKeyLegalIEC62353Code, '');
export const selectLegalCheckPCResistanceText = makeStringConfigSelector(
    ConfigConstants.configKeyLegalPCResistanceText,
    '',
);
export const selectLegalCheckPCResistanceCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalPCResistanceCode,
    '',
);
export const selectLegalCheckDeviceLeakageText = makeStringConfigSelector(
    ConfigConstants.configKeyLegalDeviceLeakageText,
    '',
);
export const selectLegalCheckDeviceLeakageCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalDeviceLeakageCode,
    '',
);
export const selectLegalCheckXRayText = makeStringConfigSelector(ConfigConstants.configKeyLegalXRayText, '');
export const selectLegalCheckXRayCode = makeStringConfigSelector(ConfigConstants.configKeyLegalXRayCode, '');
export const selectLegalCheckRadioProtectionText = makeStringConfigSelector(
    ConfigConstants.configKeyLegalRadioProtectionText,
    '',
);
export const selectLegalCheckRadioProtectionCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalRadioProtectionCode,
    '',
);
export const selectLegalCheckInstallationCheckText = makeStringConfigSelector(
    ConfigConstants.configKeyLegalInstallationCheckText,
    '',
);
export const selectLegalCheckInstallationCheckCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalInstallationCheckCode,
    '',
);
export const selectLegalCheckStatusCheckText = makeStringConfigSelector(
    ConfigConstants.configKeyLegalStatusCheckText,
    '',
);
export const selectLegalCheckStatusCheckCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalStatusCheckCode,
    '',
);
export const selectLegalCheckConsistencyCheckText = makeStringConfigSelector(
    ConfigConstants.configKeyLegalConsistencyCheckText,
    '',
);
export const selectLegalCheckConsistencyCheckCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalConsistencyCheckCode,
    '',
);
export const selectLegalCheckPartiallyCheckedText = makeStringConfigSelector(
    ConfigConstants.configKeyLegalPartiallyCheckedText,
    '',
);
export const selectLegalCheckPartiallyCheckedCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalPartiallyCheckedCode,
    '',
);
export const selectLegalCheckAcceptanceTestCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalAcceptanceTestCode,
    '',
);
export const selectLegalCheckPartialAcceptanceTestCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalPartialAcceptanceTestCode,
    '',
);
export const selectLegalCheckHealthCheckCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalHealthCheckCode,
    '',
);
export const selectLegalCheckConsistencyTestCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalConsistencyTestCode,
    '',
);
export const selectLegalCheckNEN1010Text = makeStringConfigSelector(ConfigConstants.configKeyLegalNEN1010Text, '');
export const selectLegalCheckNEN1010Code = makeStringConfigSelector(ConfigConstants.configKeyLegalNEN1010Code, '');
export const selectLegalCheckK3RoomText = makeStringConfigSelector(ConfigConstants.configKeyLegalK3RoomText, '');
export const selectLegalCheckK3RoomCode = makeStringConfigSelector(ConfigConstants.configKeyLegalK3RoomCode, '');
export const selectLegalCheckMeasurementPerformedText = makeStringConfigSelector(
    ConfigConstants.configKeyLegalMeasurementPerformedText,
    '',
);
export const selectLegalCheckMeasurementPerformedCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalMeasurementPerformedCode,
    '',
);
export const selectLegalCheckInsulationResistanceText = makeStringConfigSelector(
    ConfigConstants.configKeyLegalInsulationResistanceText,
    '',
);
export const selectLegalCheckInsulationResistanceCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalInsulationResistanceCode,
    '',
);
export const selectLegalCheckMeasurementNotNeededText = makeStringConfigSelector(
    ConfigConstants.configKeyLegalMeasurementNotNeededText,
    '',
);
export const selectLegalCheckMeasurementNotNeededCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalMeasurementNotNeededCode,
    '',
);
export const selectEowSowBreakHours = makeNumberConfigSelector(ConfigConstants.configKeyEowSowBreakHours, 0);
export const selectEowFlexibleHours = makeNumberConfigSelector(
    ConfigConstants.configKeyEowFlexibleHours,
    ConfigConstants.eowFlexibleHours,
);
export const selectFollowupDefaultAdditionalTime = makeNumberConfigSelector(
    ConfigConstants.configKeyFollowupDefaultAdditionalTime,
    Infinity,
);
export const selectFollowupMinAdditionalTime = makeNumberConfigSelector(
    ConfigConstants.configKeyFollowupMinAdditionalTime,
    Infinity,
);
export const selectFollowupAdditionalTimeStep = makeNumberConfigSelector(
    ConfigConstants.configKeyFollowupAdditionalTimeStep,
    Infinity,
);
export const selectAppsTaskCode = makeStringConfigSelector(ConfigConstants.configKeyAppsTaskCode, '');
export const selectStatusAfterFixValueForQCFlag = makeStringConfigSelector(
    ConfigConstants.configKeyStatusAfterFixValueForQCFlag,
    '',
);
export const selectEtaLimitMinutes = makeNumberConfigSelector(
    ConfigConstants.configKeyEtaLimitMinutes,
    ConfigConstants.defaultEtaLimitMinutes,
);
export const selectMaterialHistoryLimit = makeNumberConfigSelector(
    ConfigConstants.configKeyMaterialHistoryLimit,
    Infinity,
);
export const selectCorpInfoHQ1 = makeStringConfigSelector(ConfigConstants.configKeyCorpInfoHQ1, '');
export const selectCorpInfoHQ2 = makeStringConfigSelector(ConfigConstants.configKeyCorpInfoHQ2, '');
export const selectCorpInfoHQ3 = makeStringConfigSelector(ConfigConstants.configKeyCorpInfoHQ3, '');
export const selectCorpInfoHQ4 = makeStringConfigSelector(ConfigConstants.configKeyCorpInfoHQ4, '');
export const selectCorpInfoHQ5 = makeStringConfigSelector(ConfigConstants.configKeyCorpInfoHQ5, '');
export const selectCorpInfoHQ5Link = makeStringConfigSelector(ConfigConstants.configKeyCorpInfoHQ5Link, '');
export const selectCorpInfoHQTel = makeStringConfigSelector(ConfigConstants.configKeyCorpInfoHQTel, '');
export const selectCorpInfoSupportHours = makeStringConfigSelector(ConfigConstants.configKeyCorpInfoSupportHours, '');
export const selectCorpInfoSupportEmail = makeStringConfigSelector(ConfigConstants.configKeyCorpInfoSupportEmail, '');
export const selectCorpInfoSupportLink = makeStringConfigSelector(ConfigConstants.configKeyCorpInfoSupportLink, '');
export const selectCorpInfoSupportCreateTicketLink = makeStringConfigSelector(
    ConfigConstants.configKeyCorpInfoSupportCreateTicketLink,
    '',
);
export const selectCorpInfoLegalInfo1 = makeStringConfigSelector(ConfigConstants.configKeyCorpInfoLegalInfo1, '');
export const selectCorpInfoLegalInfo2 = makeStringConfigSelector(ConfigConstants.configKeyCorpInfoLegalInfo2, '');
export const selectCorpInfoLegalInfo3 = makeStringConfigSelector(ConfigConstants.configKeyCorpInfoLegalInfo3, '');
export const selectCorpInfoLegalInfo4 = makeStringConfigSelector(ConfigConstants.configKeyCorpInfoLegalInfo4, '');
export const selectCorpInfoLegalInfo5 = makeStringConfigSelector(ConfigConstants.configKeyCorpInfoLegalInfo5, '');
export const selectCorpInfoLegalInfo6 = makeStringConfigSelector(ConfigConstants.configKeyCorpInfoLegalInfo6, '');
export const selectCorpInfoLegalInfo7 = makeStringConfigSelector(ConfigConstants.configKeyCorpInfoLegalInfo7, '');
export const selectCorpInfoLegalInfoPolicy = makeStringConfigSelector(
    ConfigConstants.configKeyCorpInfoLegalInfoPolicy,
    '',
);
export const selectCorpInfoLegalInfoPolicyLink = makeStringConfigSelector(
    ConfigConstants.configKeyCorpInfoLegalInfoPolicyLink,
    '',
);
export const selectCorpInfoLegalInfoTOU = makeStringConfigSelector(ConfigConstants.configKeyCorpInfoLegalInfoTOU, '');
export const selectCorpInfoLegalInfoTOULink = makeStringConfigSelector(
    ConfigConstants.configKeyCorpInfoLegalInfoTOULink,
    '',
);
export const selectCorpInfoLegalPolicyMonth = makeStringConfigSelector(
    ConfigConstants.configKeyCorpInfoLegalPolicyMonth,
    '',
);
export const selectGlobalStorageLocation = makeStringArrayConfigSelector(
    ConfigConstants.configKeyGlobalStorageLocation,
    true,
);
export const selectGlobalStorageLocationMatPricing = makeStringArrayConfigSelector(
    ConfigConstants.configKeyGlobalStorageLocationMatPricing,
    true,
);
export const selectGlobalStorageLocationMatRequest = makeStringConfigSelector(
    ConfigConstants.configKeyGlobalStorageLocationMatRequest,
    '',
);
export const selectCIDefaultText = makeStringConfigSelector(ConfigConstants.configKeyCIDefaultText, '');
export const selectPreviousDaysTimesheetLimit = makeNumberConfigSelector(
    ConfigConstants.configKeyPreviousDaysTimesheetLimit,
    0,
);
export const selectMaxAllowedFutureTime = makeNumberConfigSelector(ConfigConstants.configKeyMaxAllowedFutureTime, 0);
export const selectDefaultFollowUpTimeP58 = makeStringConfigSelector(
    ConfigConstants.configKeyFollowupDefaultTimeP58,
    '0',
);
export const selectUseCustomisedForms = makeBooleanConfigSelector(ConfigConstants.configKeyUseCustomisedForms, false);
export const selectLegalCheckVDE701702Text = makeStringConfigSelector(ConfigConstants.configKeyLegalVDE701702Text, '');
export const selectLegalCheckVDE701702Code = makeStringConfigSelector(ConfigConstants.configKeyLegalVDE701702Code, '');
export const selectLegalCheckVDEPCResistanceText = makeStringConfigSelector(
    ConfigConstants.configKeyLegalVDEPCResistanceText,
    '',
);
export const selectLegalCheckVDEPCResistanceCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalVDEPCResistanceCode,
    '',
);
export const selectLegalCheckVDEDeviceLeakageText = makeStringConfigSelector(
    ConfigConstants.configKeyLegalVDEDeviceLeakageText,
    '',
);
export const selectLegalCheckVDEDeviceLeakageCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalVDEDeviceLeakageCode,
    '',
);
export const selectLegalCheckVDEInsulationResistanceText = makeStringConfigSelector(
    ConfigConstants.configKeyLegalVDEInsulationResistanceText,
    '',
);
export const selectLegalCheckVDEInsulationResistanceCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalVDEInsulationResistanceCode,
    '',
);
export const selectPredefinedEmailDomainName = makeStringConfigSelector(
    ConfigConstants.configKeyPredefinedEmailDomainName,
    '',
);
export const selectUseNoCustomErrorCode = makeBooleanConfigSelector(
    ConfigConstants.configKeyUseNoCustomErrorCode,
    false,
);
export const selectUseAddNewMaterialReturn = makeBooleanConfigSelector(
    ConfigConstants.configKeyUseAddNewMaterialReturn,
    false,
);
export const selectReportTextLineLength = makeNumberConfigSelector(
    ConfigConstants.configKeyReportTextLineLength,
    ConfigConstants.defaultLineLength,
);
export const selectReportTextMaxLines = makeNumberConfigSelector(ConfigConstants.configKeyReportTextMaxLines, Infinity);
export const selectMaxRecipientEntries = makeNumberConfigSelector(
    ConfigConstants.configKeyMaxRecipientEntries,
    ConfigConstants.maxRecipientEntries,
);
export const selectSelectDefaultFollowupReason = makeBooleanConfigSelector(
    ConfigConstants.configKeySelectDefaultFollowupReason,
    true,
);
export const selectEnableSafetyAssessmentForm = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableSafetyAssessmentForm,
    false,
);
export const selectBootstockReplenishEnabled = makeBooleanConfigSelector(
    ConfigConstants.configKeyBootstockReplenishEnabled,
    false,
);
export const selectSafetyAssessmentForm = makeStringConfigSelector(ConfigConstants.configKeySafetyAssessmentForm, '');
export const selectEnablePaceFormsCrossDeviceSync = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnablePaceFormsCrossDeviceSync,
    false,
);
export const selectDisplayAdditionalMaterialRequestInformation = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayAdditionalMatReqInfo,
    false,
);
export const selectAddMaterialCurrencyCode = makeStringConfigSelector(
    ConfigConstants.configKeyAddMaterialCurrencyCode,
    '',
);
export const selectAddMaterialSelectBillableDefault = makeBooleanConfigSelector(
    ConfigConstants.configKeyAddMaterialSelectBillableDefault,
    false,
);
export const selectAddMaterialBillingIndicator = makeStringConfigSelector(
    ConfigConstants.configKeyAddMaterialBillingIndicator,
    '',
);
export const selectAddMaterialBillingChangeable = makeBooleanConfigSelector(
    ConfigConstants.configKeyAddMaterialBillingChangeable,
    false,
);
export const selectShowCommercialInfo = makeBooleanConfigSelector(ConfigConstants.configKeyShowCommercialInfo, true);
export const selectCSMLTrackerLink = makeStringConfigSelector(ConfigConstants.configKeyCSMLTrackerLink, '');
export const selectAddDeliveryAddressTaxi = makeBooleanConfigSelector(
    ConfigConstants.configKeyAddDeliveryAddressTaxi,
    true,
);
export const selectDisablePaceForms = makeBooleanConfigSelector(ConfigConstants.configKeyDisablePaceForms, false);
export const selectDisplayNonProductiveHours = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayNonProductiveHours,
    true,
);
export const selectDisplayAdditionalTravelBack = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayAdditionalTravelBack,
    false,
);
export const selectDisplayTravelBackSummary = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayTravelBackSummary,
    false,
);
export const selectDisplayAdditionalTravelInfo = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayAdditionalTravelInfo,
    false,
);
export const selectDisplayMeansOfTransport = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayMeansOfTransport,
    false,
);
export const selectDisplayAdditionalMaterialReturnInformation = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayAdditionalMatReturnInfo,
    false,
);
export const selectFormatMultilineTextPattern = makeStringConfigSelector(
    ConfigConstants.configKeyFormatMultilineTextPattern,
    '',
);
export const selectFormatInternalInfoMultilineTextPattern = makeStringConfigSelector(
    ConfigConstants.configKeyFormatInternalInfoMultilineTextPattern,
    '',
);
export const selectFormatMomentInUTC = makeBooleanConfigSelector(ConfigConstants.configKeyFormatMomentInUTC, true);
export const selectDisplayTimesheetAddress = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayTimesheetAddress,
    false,
);
export const selectTravelBackStartEditable = makeBooleanConfigSelector(
    ConfigConstants.configKeyTravelBackStartEditable,
    false,
);
export const selectTravelBackEndEditable = makeBooleanConfigSelector(
    ConfigConstants.configKeyTravelBackEndEditable,
    true,
);
export const selectTimesheetEndTimeLimit = makeStringConfigSelector(ConfigConstants.configKeyTimesheetEndTimeLimit, '');
export const selectDisplayTimesheetAddressOptions = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayTimesheetAddressOptions,
    false,
);
export const selectTimesheetAddressHome = makeStringConfigSelector(ConfigConstants.configKeyTimesheetAddressHome, '');
export const selectTimesheetAddressOffice = makeStringConfigSelector(
    ConfigConstants.configKeyTimesheetAddressOffice,
    '',
);
export const selectMatPrioritiesForDOP = makeStringConfigSelector(ConfigConstants.configKeyMatPrioritiesForDOP, '');
export const selectMatPrioritiesForCustomer = makeStringConfigSelector(
    ConfigConstants.configKeyMatPrioritiesForCustomer,
    '',
);
export const selectDisplayMaterialQuote = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayMaterialQuote,
    false,
);
export const selectDisplayCustomerSignatureInForm = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayCustomerSignatureInForm,
    false,
);
export const selectSparePartsCatalogLink = makeStringConfigSelector(ConfigConstants.configKeySparePartsCatalogLink, '');
export const selectDefaultReasonCodeForCentralScrap = makeStringConfigSelector(
    ConfigConstants.configKeyDefaultReasonCodeForCentralScrap,
    '',
);
export const selectDisableSpecialTransportAWBfield = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisableSpecialTransportAWBfield,
    false,
);
export const selectFollowUpDefaultDays = makeNumberConfigSelector(ConfigConstants.configKeyFollowUpDefaultDays, 1);
export const selectDisplayEtaAlert = makeBooleanConfigSelector(ConfigConstants.configKeyDisplayEtaAlert, false);
export const selectEtaAlertTimerLimit = makeNumberConfigSelector(
    ConfigConstants.configKeyEtaAlertTimerLimit,
    ConfigConstants.defaultEtaAlertTimerLimit,
);
export const selectTextClausePattern = makeStringConfigSelector(ConfigConstants.configKeyTextClausePattern, '');
export const selectShowMobileNumberForParticipants = makeBooleanConfigSelector(
    ConfigConstants.configKeyShowMobileNumberForParticipants,
    false,
);
export const selectShowCalibrationDate = makeBooleanConfigSelector(ConfigConstants.configKeyShowCalibrationDate, false);
export const selectUseCustomTimeInput = makeBooleanConfigSelector(ConfigConstants.configKeyUseCustomTimeInput, false);
export const selectShowCustomerInformationOnSummary = makeBooleanConfigSelector(
    ConfigConstants.configKeyShowCustomerInformationOnSummary,
    false,
);
export const selectFormatMultilineTextOnBlur = makeBooleanConfigSelector(
    ConfigConstants.configKeyFormatMultilineTextOnBlur,
    false,
);
export const selectShowFormCustomerEmailData = makeBooleanConfigSelector(
    ConfigConstants.configKeyShowFormCustomerEmailData,
    false,
);
export const selectUseCustomDurationStepper = makeBooleanConfigSelector(
    ConfigConstants.configKeyUseCustomDurationStepper,
    false,
);
export const selectPrefillTroubleText = makeBooleanConfigSelector(ConfigConstants.configKeyPrefillTroubleText, true);
export const selectEnableJobOnFly = makeBooleanConfigSelector(ConfigConstants.configKeyEnableJobOnFly, false);
export const selectJobOnFlyDefaultDuration = makeNumberConfigSelector(
    ConfigConstants.configKeyJobOnFlyDefaultDuration,
    ConfigConstants.jobOnFlyDefaultDuration,
);
export const selectDisplayMaterialPricing = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayMaterialPricing,
    false,
);
export const selectActivateCosima = makeBooleanConfigSelector(ConfigConstants.configKeyActivateCosima, false);
export const selectDisplayBatchNumber = makeBooleanConfigSelector(ConfigConstants.configKeyDisplayBatchNumber, false);
export const selectDisplayWeeklyWorkTime = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayWeeklyWorkTime,
    false,
);
export const selectEnableStandbyTravel = makeBooleanConfigSelector(ConfigConstants.configKeyEnableStandbyTravel, false);
export const selectDisplayCentralScrap = makeBooleanConfigSelector(ConfigConstants.configKeyDisplayCentralScrap, true);
export const selectDisplayCentralScrapForDangerousGoods = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayCentralScrapDangerGoods,
    false,
);
export const selectSmartSensorTracker = makeStringArrayConfigSelector(
    ConfigConstants.configKeySmartSensorTracker,
    true,
    [''],
);
export const selectSmartSensorTrackerUrl = makeStringConfigSelector(ConfigConstants.configKeySmartSensorTrackerUrl, '');
export const selectMatSerTypeaheadToggle = makeBooleanConfigSelector(
    ConfigConstants.configKeyMatSerTypeaheadToggle,
    false,
);
export const selectMatLTextDisplayLines = makeStringArrayConfigSelector(ConfigConstants.configKeyMatLTextDisplayLines);
export const selectDisplayAdditionalTravelCost = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayAdditionalTravelCost,
    false,
);
export const selectAdditionalTravelCostText = makeStringConfigSelector(
    ConfigConstants.configKeyAdditionalTravelCostText,
    '',
);
export const selectDisplayCseSignature = makeBooleanConfigSelector(ConfigConstants.configKeyDisplayCseSignature, false);
export const selectDisplayBootStockIndicator = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayBootStockIndicator,
    false,
);
export const selectEnableBootstockScrap = makeBooleanConfigSelector(
    ConfigConstants.configKeyBootStockScrapEnabled,
    false,
);
export const selectAllowNotificationPause = makeBooleanConfigSelector(
    ConfigConstants.configKeyAllowNotificationPause,
    false,
);
export const selectDisplayTrendCodeInFollowup = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayTrendCodeInFollowup,
    false,
);
export const selectShowMeasurementToolsDetails = makeBooleanConfigSelector(
    ConfigConstants.configKeyShowMeasurementToolsDetails,
    false,
);
export const selectShowDataMediumInEquipment = makeBooleanConfigSelector(
    ConfigConstants.configKeyShowDataMediumInEquipment,
    false,
);
export const selectAnaLink = makeStringConfigSelector(ConfigConstants.configKeyAnaLink, '');
export const selectShowIconForMaterialDelivery = makeBooleanConfigSelector(
    ConfigConstants.configKeyShowIconForMaterialDelivery,
    false,
);
export const selectTeamplayFleetLink = makeStringConfigSelector(ConfigConstants.configKeyTeamplayFleetLink, '');
export const selectSparePartCatalogMatLink = makeStringConfigSelector(
    ConfigConstants.configKeySparePartCatalogMatLink,
    '',
);
export const selectEnableTimesheetEmail = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableTimesheetEmail,
    false,
);
export const selectDataManagementEmail = makeStringConfigSelector(ConfigConstants.configKeyDataManagementEmail, '');
export const selectShowStartImmediately = makeBooleanConfigSelector(
    ConfigConstants.configKeyShowStartImmediately,
    true,
);
export const selectShowPlaceHolderForReportText = makeBooleanConfigSelector(
    ConfigConstants.configKeyShowPlaceHolderForReportText,
    false,
);
export const selectShowPlaceHolderForEscalationText = makeBooleanConfigSelector(
    ConfigConstants.configKeyShowPlaceHolderForEscalationText,
    false,
);
export const selectMaxWorkingHoursInTimesheet = makeNumberConfigSelector(
    ConfigConstants.configKeyMaxWorkingHoursInTimesheet,
    ConfigConstants.totalWorkingHours,
);
export const selectBackendName = makeStringConfigSelector(ConfigConstants.configKeyBackendName, '');
export const selectLegal3551DetachPowerCordText = makeStringConfigSelector(
    ConfigConstants.configKeyLegal3551DetachPowerCordText,
    '',
);
export const selectLegal3551PermanentConnectedText = makeStringConfigSelector(
    ConfigConstants.configKeyLegal3551PermanentConnectedText,
    '',
);
export const selectLegal3551NonDetachPowerCordText = makeStringConfigSelector(
    ConfigConstants.configKeyLegal3551NonDetachPowerCordText,
    '',
);
export const selectLegal3551DetachPowerCordCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegal3551DetachPowerCordCode,
    '',
);
export const selectLegal3551PermanentConnectedCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegal3551PermanentConnectedCode,
    '',
);
export const selectLegal3551NonDetachPowerCordCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegal3551NonDetachPowerCordCode,
    '',
);
export const selectLegalVerifiedSecurityDeviceText = makeStringConfigSelector(
    ConfigConstants.configKeyLegalVerifiedSecurityDeviceText,
    '',
);
export const selectLegalVerifiedSecurityDeviceCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalVerifiedSecurityDeviceCode,
    '',
);
export const selectLegalResumptionAfterUserValidationText = makeStringConfigSelector(
    ConfigConstants.configKeyLegalResumptionUserValidText,
    '',
);
export const selectLegalResumptionAfterUserValidationCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalResumptionUserValidCode,
    '',
);
export const selectLegalCheckHandoverSafetyInformationText = makeStringConfigSelector(
    ConfigConstants.configKeyLegalHandoverSafetyInfoText,
    '',
);
export const selectLegalCheckHandoverUserDocumentationText = makeStringConfigSelector(
    ConfigConstants.configKeyLegalHandoverUserDocText,
    '',
);
export const selectLegalCheckHandoverSafetyInformationCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalHandoverSafetyInfoCode,
    '',
);
export const selectLegalCheckHandoverUserDocumentationCode = makeStringConfigSelector(
    ConfigConstants.configKeyLegalHandoverUserDocCode,
    '',
);
export const selectDisplayConfirmSignatures = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayConfirmSignatures,
    false,
);
export const selectEnableRemoteWork = makeBooleanConfigSelector(ConfigConstants.configKeyEnableRemoteWork, false);
export const selectShowRemoteStatusChangeWarning = makeBooleanConfigSelector(
    ConfigConstants.configKeyShowRemoteStatusChangeWarning,
    false,
);
export const selectDisplayDocBox = makeBooleanConfigSelector(ConfigConstants.configKeyDisplayDocBox, false);
export const selectUploadToDocBoxStatusMandatory = makeBooleanConfigSelector(
    ConfigConstants.configKeyUploadToDocBoxStatusMandatory,
    false,
);
export const selectMatQuoteDistanceUnit = makeStringConfigSelector(ConfigConstants.configKeyMatQuoteDistanceUnit, 'km');
export const selectEnableMatQuoteDistance = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableMatQuoteDistance,
    false,
);
export const selectEnableHideTimesheetAddress = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableHideTimesheetAddress,
    false,
);
export const selectHideTimesheetPersonnelSubArea = makeStringConfigSelector(
    ConfigConstants.configKeyHideTimesheetPersonnelSubArea,
    '',
);
export const selectShowTpfReportVisibility = makeBooleanConfigSelector(
    ConfigConstants.configKeyShowTpfReportVisibility,
    false,
);
export const selectMandatoryTpfReportVisibility = makeBooleanConfigSelector(
    ConfigConstants.configKeyMandatoryTpfReportVisibility,
    false,
);
export const selectEquipmentErrorCodeRemoteWork = makeStringConfigSelector(
    ConfigConstants.configKeyEquipmentErrorCodeRemoteWork,
    '',
);
export const selectDisplayConfirmationDistance = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayConfirmationDistance,
    false,
);
export const selectDisplayConfirmationExpenses = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayConfirmationExpenses,
    false,
);
export const selectEnableIndividualDocBoxDownload = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableIndividualDocBoxDownload,
    false,
);
export const selectPlaceHolderForCustomerIdentification = makeStringConfigSelector(
    ConfigConstants.configKeyPlaceHolderForCustomerIdentification,
    '---',
);
export const selectShowDataMaintenanceEnhancement = makeBooleanConfigSelector(
    ConfigConstants.configKeyShowDataMaintenanceEnhancement,
    false,
);
export const selectDisplayServicePartQuestionnaire = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayServicePartQuestionnaire,
    false,
);
export const selectMaterialOrderTextInfoLineLength = makeNumberConfigSelector(
    ConfigConstants.configKeyMaterialOrderTextInfoLineLength,
    ConfigConstants.defaultLineLength,
);
export const selectMaterialOrderTextInfoMaxLines = makeNumberConfigSelector(
    ConfigConstants.configKeyMaterialOrderTextInfoMaxLines,
    Infinity,
);
export const selectMaterialOrderTextInfoPattern = makeStringConfigSelector(
    ConfigConstants.configKeyMaterialOrderTextInfoPattern,
    '',
);
export const selectCSMLInvivoPhoneNumber = makeStringConfigSelector(ConfigConstants.configKeyCSMLInvivoPhoneNumber, '');
export const selectCSMLInvitroPhoneNumber = makeStringConfigSelector(
    ConfigConstants.configKeyCSMLInvitroPhoneNumber,
    '',
);
export const selectEnableTextInfoValidation = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableTextInfoValidation,
    false,
);
export const selectByBoxKonnectLink = makeStringConfigSelector(ConfigConstants.configKeyByBoxKonnectLink, '');
export const selectCBDocLink = makeStringConfigSelector(ConfigConstants.configKeyCBDocLink, '');
export const selectSeSoLink = makeStringConfigSelector(ConfigConstants.configKeySeSoLink, '');
export const selectSeSoDivisions = makeStringArrayConfigSelector(ConfigConstants.configKeySeSoDivisions, true);
export const selectEnableMatReturnWorkAround = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableMatReturnWorkAround,
    false,
);
export const selectDisplayAddMaterialButton = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayAddMaterialButton,
    false,
);
export const selectEnableBillingIndicator = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableBillingIndicator,
    false,
);
export const selectDisplayCustomerPrice = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplayCustomerPrice,
    false,
);
export const selectEnableManualIndividualization = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableManIndividualization,
    false,
);
export const selectEnableDocumentFeedbackButton = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableDocumentFeedbackButton,
    false,
);
export const selectEnableFormsHistoricalValues = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableHistoricalValues,
    false,
);
export const selectMeasurementToolsDefaultSettingsAPPS = makeBooleanConfigSelector(
    ConfigConstants.configKeyMeasurementToolsDefaultSettingsAPPS,
    false,
);
export const selectMaterialOrderDeliveryOptions = makeStringConfigSelector(
    ConfigConstants.configKeyMaterialOrderDeliveryOptions,
    '',
);
export const selectMaterialReturnAddressOptions = makeStringConfigSelector(
    ConfigConstants.configKeyMaterialReturnAddressOptions,
    '',
);
export const selectBKInfoTextForBillingIndicatorsNotReceived = makeStringConfigSelector(
    ConfigConstants.configKeyBKInfoTextForBillingIndicatorsNotReceived,
    '',
);
export const selectShowCyberSecurityQuestion = makeBooleanConfigSelector(
    ConfigConstants.configKeyShowCyberSecurityQuestion,
    false,
);
export const selectHideAbortAllFormsButton = makeBooleanConfigSelector(
    ConfigConstants.configKeyHideAbortAllFormsButton,
    false,
);
export const selectEnableAbortFormInList = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableAbortFormInList,
    false,
);
export const selectCommercialInfMandatoryForNonBill = makeBooleanConfigSelector(
    ConfigConstants.configKeyCommercialInfMandatoryForNonBill,
    false,
);
export const selectEnableTextInfoForDop = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableTextInfoForDop,
    false,
);
export const selectHideSubmitButtonForForms = makeBooleanConfigSelector(
    ConfigConstants.configKeyHideSubmitButtonForForms,
    false,
);
export const selectHideAuthorsFormsButtons = makeBooleanConfigSelector(
    ConfigConstants.configKeyHideAuthorsFormsButtons,
    false,
);
export const selectLegalCheckDosageCode = makeStringConfigSelector(ConfigConstants.configKeyLegalDosageCode, '');
export const selectLegalCheckDosageText = makeStringConfigSelector(ConfigConstants.configKeyLegalDosageText, '');
export const selectEnableStatesOfUSADropdown = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableStatesOfUSADropdown,
    false,
);
export const selectDeliveryOptionsEnabled = makeBooleanConfigSelector(
    ConfigConstants.configKeyDeliveryOptionsEnabled,
    false,
);
export const selectDeliveryApprovalMinimumLength = makeNumberConfigSelector(
    ConfigConstants.configKeyDeliveryApprovalMinimumLength,
    ConfigConstants.defaultCustomerApproverLength,
);
export const selectEnabledAfterHoursCallOut = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableAfterHourCallOut,
    false,
);
export const selectEnableOtherTasks = makeBooleanConfigSelector(ConfigConstants.configKeyEnableOtherTasks, false);
export const selectOtherTasksOverlapUpcomingNoti = makeBooleanConfigSelector(
    ConfigConstants.configKeyGetOtherTasksOverlapUpcomingNotification,
    true,
);
export const selectDisplaySideMenuDocBoxFlag = makeBooleanConfigSelector(
    ConfigConstants.configKeyDisplaySideMenuDocBoxFlag,
    false,
);
export const selectParticipantCreditTypeSelectorEnabled = makeBooleanConfigSelector(
    ConfigConstants.configKeyParticipantCreditTypeSelectorEnabled,
    false,
);
export const selectEnableDurationChange = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableDurationChange,
    false,
);
export const selectEnableDeliveryCutoffTime = makeBooleanConfigSelector(
    ConfigConstants.configKeyDeliveryCutoffTimeEnabled,
    false,
);
export const selectDocMissingProtocolsEnabled = makeBooleanConfigSelector(
    ConfigConstants.configKeyDocMissingProtocolsEnabled,
    false,
);
export const selectEnableFDA2579Form = makeBooleanConfigSelector(ConfigConstants.configKeyEnableFDA2579Form, false);
export const selectCentricarePCHEnabled = makeBooleanConfigSelector(
    ConfigConstants.configKeyCentricarePCHEnabled,
    false,
);
export const selectCentricarePCHWarningTime = makeNumberConfigSelector(
    ConfigConstants.configKeyCentricarePCHWarningTime,
    ConfigConstants.initialCentricarePCHWarningTime,
);
export const selectRadioactiveSurveyEnabled = makeBooleanConfigSelector(
    ConfigConstants.configKeyRadioactiveSurveyEnabled,
    false,
);
export const selectPickupRFIDEnabled = makeBooleanConfigSelector(
    ConfigConstants.configKeyMaterialPickupRFIDEnabled,
    false,
);
export const selectEnableAWBPackaging = makeBooleanConfigSelector(ConfigConstants.configKeyAWBPackagingEnabled, false);
export const selectEnableFedexAwbPackagingScanning = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableFedexAwbPackaging,
    false,
);
export const selectEnableInstallationPaperwork = makeBooleanConfigSelector(
    ConfigConstants.configKeyInstallationPaperworkEnabled,
    false,
);
export const selectIseCseMetricFormPowerApp = makeStringConfigSelector(
    ConfigConstants.configKeyIseCseMetricFormAppLink,
    '',
);
export const selectEnableCommercialInfoMandatory = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableCommercialInfoMandatory,
    false,
);
export const selectShowRemoteWork = makeBooleanConfigSelector(ConfigConstants.configKeyShowRemoteWork, false);
export const selectShowStartImmediatelyFollowUp = makeBooleanConfigSelector(
    ConfigConstants.configKeyShowStartImmediatelyFollowUp,
    false,
);
export const selectEnablePhysicalInventory = makeBooleanConfigSelector(
    ConfigConstants.configKeyPhysicalInventoryEnabled,
    false,
);
export const selectRentalToolAllowWithNoConsumption = makeBooleanConfigSelector(
    ConfigConstants.configKeyRentalToolAllowWithNoConsumption,
    false,
);
export const selectEnableBootstockReturn = makeBooleanConfigSelector(
    ConfigConstants.configKeyBootstockReturnEnabled,
    false,
);
export const selectAppBookingForPartners = makeBooleanConfigSelector(
    ConfigConstants.configKeyAppBookingForPartners,
    false,
);
export const selectAppBookingEnabled = makeBooleanConfigSelector(ConfigConstants.configKeyAppBookingEnabled, false);
export const selectAppBookingMinDate = makeNumberConfigSelector(ConfigConstants.configKeyAppBookingMinDate, 0);
export const selectAppBookingMaxDate = makeNumberConfigSelector(ConfigConstants.configKeyAppBookingMaxDate, Infinity);
export const selectAppBookingMaxDuration = makeNumberConfigSelector(
    ConfigConstants.configKeyAppBookingMaxDuration,
    Infinity,
);
export const selectAppBookingShowDedicated = makeBooleanConfigSelector(
    ConfigConstants.configKeyAppBookingShowDedicated,
    false,
);
export const selectAppBookingDefaultDedicated = makeBooleanConfigSelector(
    ConfigConstants.configKeyAppBookingDefaultDedicated,
    false,
);
export const selectAppBookingITCS = makeStringConfigSelector(ConfigConstants.configKeyAppBookingITCS, '');
export const selectEnableForceCheckin = makeBooleanConfigSelector(ConfigConstants.configKeyEnableForceCheckin, false);
export const selectEnableMeasurementToolsForSCRM = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableMeasurementToolsForSCRM,
    false,
);
export const selectEnableUnlimitedHandover = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableUnlimitedHandOver,
    false,
);
export const selectEnableSmartlocker = makeBooleanConfigSelector(ConfigConstants.configKeySmartlockerEnabled, false);
export const selectSmartlockerMaterialIdentifier = makeStringConfigSelector(
    ConfigConstants.configKeySmartlockerIdentifier,
    ConfigConstants.defaultSmartlockerIdentifier,
);

// --- bespoke config selectors: keys needing more than a plain type coercion ---
export const selectShowFollowupLongText = makeBooleanConfigSelector(
    ConfigConstants.configKeyShowFollowupLongText,
    false,
);
export const selectShowFollowupAdditionalInfo = makeBooleanConfigSelector(
    ConfigConstants.configKeyShowFollowupAdditionalInfo,
    false,
);
export const selectIsTimesheetDisabled = makeBooleanConfigSelector(ConfigConstants.configKeyDisableTimesheet, false);
export const selectHideParticipantFormSubmission = makeBooleanConfigSelector(
    ConfigConstants.configHideParticipantFormSubmission,
    false,
);
export const selectShowFleetLinkInEquipmentHistory = makeBooleanConfigSelector(
    ConfigConstants.showFleetLinkInEquipmentHistory,
    false,
);
export const selectIsCustomerDamageEnabled = makeBooleanConfigSelector(
    ConfigConstants.configKeyCustomerDamageEnabled,
    false,
);
export const selectIsFocReplacementEnabled = makeBooleanConfigSelector(
    ConfigConstants.configKeyFocReplacementEnabled,
    false,
);
export const selectIsSourceReplacementEnabled = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableSourceReplacement,
    false,
);
export const selectNotificationsHistoryDaysLimit = createSelector(selectConfigEntries, (entries): number => {
    const value = entries[ConfigConstants.configKeyNotificationHistoryDays];
    return value ? parseInt(value, 10) : 30;
});
export const selectMaxWeeklyWorkingHours = createSelector(selectConfigEntries, (entries): number => {
    const value = entries[ConfigConstants.configKeyMaxWeeklyWorkingHours];
    return value ? parseInt(value, 10) : 50;
});
export const selectDefaultTpfReportVisibility = createSelector(selectConfigEntries, (entries): number => {
    const value = entries[ConfigConstants.configKeyDefaultTpfReportVisibility];
    return value ? parseInt(value, 10) : 2;
});
export const selectDowntimeThresholdInMinutes = createSelector(selectConfigEntries, (entries): number => {
    const value = entries[ConfigConstants.configKeyDowntimeThresholdInMinutes];
    return value ? parseInt(value, 10) : -1;
});
export const selectUserCountryFI = makeStringConfigSelector(ConfigConstants.userCountryFI, '');
export const selectDocBoxInstallationDocType = makeStringConfigSelector(
    ConfigConstants.configKeyDocBoxInstallationType,
    '',
);
export const selectDocBoxEnableReportTypeSelection = makeBooleanConfigSelector(
    ConfigConstants.configKeyDocBoxEnableReportTypeSelection,
    false,
);
export const selectRadioactiveSurveyToolCalibration = createSelector(
    selectConfigEntries,
    (entries): number | undefined => {
        const value = entries[ConfigConstants.configKeyRadioactiveSurveyToolCalibration];
        return value ? parseFloat(value) : undefined;
    },
);
export const selectEarlyTravelHours = createSelector(selectConfigEntries, (entries): number => {
    const value = entries[ConfigConstants.configKeyEarlyTravelHours];
    if (!value || value === '') {
        return 0;
    }
    const hours = Number(value);
    return isNaN(hours) ? 0 : hours;
});
export const selectDisclaimerForChinaFI = createSelector(selectConfigEntries, (entries): string => {
    const value = entries[ConfigConstants.disclaimerForChinaFI];
    return value ? value.replace(/\\n/g, '\n') : '';
});
export const selectDataManagementEmailInvitro = createSelector(
    selectConfigEntries,
    selectDataManagementEmail,
    (entries, fallback): string => {
        const value = entries[ConfigConstants.configKeyDataManagementEmailInvitro];
        return value && value.length > 0 ? value : fallback;
    },
);
export const selectDocboxDoctypes = createSelector(
    selectConfigEntries,
    (entries): Array<{ key: string; value: string }> =>
        Object.keys(entries)
            .filter((key) => key.startsWith('DocboxDoctype_'))
            .map((key) => ({ key: key.toUpperCase(), value: entries[key] })),
);
export const selectUploadToDocBoxAllowedMimeTypes = createSelector(selectConfigEntries, (entries): string[] => {
    const value = entries[ConfigConstants.configKeyUploadToDocBoxAllowedMimeTypes];
    return value ? value.split(',') : ConfigConstants.defaultAllowedMimeTypes;
});
export const selectReportAnIssueAllowedMimeTypes = createSelector(selectConfigEntries, (entries): string[] => {
    const value = entries[ConfigConstants.configKeyReportAnIssueAllowedMimeTypes];
    return value ? value.split(',') : ConfigConstants.defaultAllowedMimeTypes;
});
export const selectMatRequestSubAddressCharLimit = createSelector(selectConfigEntries, (entries): number => {
    const value = entries[ConfigConstants.configKeyMatRequestSubAddressCharLimit];
    return value ? parseInt(value, 10) : ConfigConstants.subAddressMaxCharLimit;
});
export const selectMatRequestContactCharLimit = createSelector(selectConfigEntries, (entries): number => {
    const value = entries[ConfigConstants.configKeyMatRequestContactCharLimit];
    return value ? parseInt(value, 10) : ConfigConstants.contactMaxCharLimit;
});
export const selectEmojisRegex = createSelector(selectConfigEntries, (entries): string => {
    const configRegex = entries[ConfigConstants.configKeyEmojisRegex] ?? ConfigConstants.defaultEmojiRegex;
    // Matches UTF-16 High and Low surrogate pairs to detect emojis (characters above U+FFFF)
    const surrogateEmojiRegex = `[\\uD800-\\uDBFF][\\uDC00-\\uDFFF]`;
    return `${surrogateEmojiRegex}|${configRegex}|${emojiRegex().source}`;
});
export const selectComplaintsQuestionnaireLink = (appID = ''): MemoizedSelector<object, string> =>
    makeStringConfigSelector(ConfigConstants.configKeyComplaintsQuestionnaireLink + appID, '');

// parameterized selector factories (accept the getter argument)
export const selectComplaintNotificationType = (
    notificationType: ConfigConstants.NotificationTypes,
): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyComplaintNotifTypes];
        return value && notificationType.length > 0 ? value.includes(notificationType) : false;
    });
export const selectShowEquipment = (taskGroup: string): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyShowEquipment];
        return value && taskGroup.length > 0 ? value.includes(taskGroup) : true;
    });
export const selectShowMaterialConsumption = (taskGroup: string): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyShowMaterialConsumption];
        return value && taskGroup.length > 0 ? value.includes(taskGroup) : true;
    });
export const selectShowMeasurementTools = (taskGroup: string): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyShowMeasurementTools];
        return value && taskGroup.length > 0 ? value.includes(taskGroup) : true;
    });
export const selectShowParticipantList = (taskGroup: string): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyShowParticipantList];
        return value && taskGroup.length > 0 ? value.includes(taskGroup) : false;
    });
export const selectShowFollowup = (taskGroup: string): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyShowFollowup];
        return value && taskGroup.length > 0 ? value.includes(taskGroup) : false;
    });
export const selectShowMeasuringPoints = (taskGroup: string): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyShowMeasuringPoints];
        return value && taskGroup.length > 0 ? value.includes(taskGroup) : false;
    });
export const selectDivisionsWithAdditionalLegalText = (division: string): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyDivisionsWithAdditionalLegalText];
        return value && division.length > 0 ? value.includes(division) : false;
    });
export const selectDivisionsWithIEC62353 = (division: string): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyDivisionsWithIEC62353];
        return value && division.length > 0 ? value.includes(division) : false;
    });
export const selectDivisionsWithVDE701702 = (division: string): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyDivisionsWithVDE701702];
        return value && division.length > 0 ? value.includes(division) : false;
    });

/**
 * The configured pause codes as the raw configured string, for callers that test membership
 * themselves — the pure submit gates do, so they need the list rather than a per-code selector.
 */
export const selectTimesheetPauseCodeList = makeStringConfigSelector(ConfigConstants.configKeyTimesheetPauseCodes, '');

const isTimesheetPauseCode = (configuredPauseCodes: string | undefined, taskType: string): boolean =>
    configuredPauseCodes !== undefined && taskType.length > 0 ? configuredPauseCodes.includes(taskType) : false;

export const selectTimesheetPauseCodes = (taskType: string): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyTimesheetPauseCodes];
        return value && taskType.length > 0 ? value.includes(taskType) : false;
    });

/**
 * Predicate form of {@link selectTimesheetPauseCodes}, for consumers that test many task types
 * in one projection — the factory above builds a fresh, unmemoized selector per task type.
 */
export const selectIsTimesheetPauseCode: MemoizedSelector<object, (taskType: string) => boolean> = createSelector(
    selectConfigEntries,
    (entries) => (taskType: string) =>
        isTimesheetPauseCode(entries[ConfigConstants.configKeyTimesheetPauseCodes], taskType),
);
export const selectDisableQuoteForNotificationTypes = (
    notificationType: ConfigConstants.NotificationTypes,
): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyDisableQuoteForNotificationTypes];
        return value && notificationType.length > 0 ? value.includes(notificationType) : false;
    });
export const selectEscalateNotificationTypes = (
    notificationType: ConfigConstants.NotificationTypes,
): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyEscalateNotificationTypes];
        return value && notificationType.length > 0 ? value.includes(notificationType) : true;
    });
export const selectSpecialDangerousGoodsInd = (dangerousGoodInd: string): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeySpecialDangerousGoodsInd];
        return value && dangerousGoodInd.length > 0 ? value.includes(dangerousGoodInd) : false;
    });
export const selectDangerousGoodsIndicator = (dangerousGoodInd: string): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyDangerousGoodsInd];
        return value && dangerousGoodInd.length > 0 ? value.includes(dangerousGoodInd) : false;
    });
/**
 * Whether a semicolon-separated config value lists `candidate`.
 *
 * Extracted so a caller that has to answer this for many candidates at once can share the exact
 * predicate the per-candidate selectors use, instead of building a factory selector per candidate
 * inside a projector — which would defeat memoization, since each call returns a new selector.
 *
 * @param configValue the raw config value, absent when the key is not configured
 * @param candidate the value to look for
 * @returns whether the config value lists it
 */
export const configValueIncludes = (configValue: string | undefined, candidate: string): boolean =>
    configValue && candidate.length > 0 ? configValue.includes(candidate) : false;

/** The raw `IsTravelBillable` order-type list, for callers resolving many order types at once. */
export const selectTravelBillableOrderTypes = createSelector(
    selectConfigEntries,
    (entries) => entries[ConfigConstants.configKeyIsTravelBillable],
);

export const selectIsTravelBillable = (orderType: string): MemoizedSelector<object, boolean> =>
    createSelector(selectTravelBillableOrderTypes, (value) => configValueIncludes(value, orderType));
export const selectLegal3551NotificationTypes = (
    notificationType: ConfigConstants.NotificationTypes,
): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyLegal3551NotificationTypes];
        return value && notificationType.length > 0 ? value.includes(notificationType) : false;
    });
export const selectLegalVerificationNotificationTypes = (
    notificationType: ConfigConstants.NotificationTypes,
): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyLegalVerificationNotifTypes];
        return value && notificationType.length > 0 ? value.includes(notificationType) : false;
    });
export const selectDivisionsWithVerificationLegalCheck = (division: string): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyDivisionsWithVerifLegalCheck];
        return value && division.length > 0 ? value.includes(division) : false;
    });
export const selectShowUploadToDocBoxForNotificationType = (
    notificationType: ConfigConstants.NotificationTypes,
): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyShowUploadToDocBoxForNotificationType];
        return value && notificationType.length > 0 ? value.includes(notificationType) : false;
    });
export const selectUploadToDocBoxMandatoryForNotificationType = (
    notificationType: ConfigConstants.NotificationTypes,
): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyUploadToDocBoxMandatoryForNotificationType];
        return value && notificationType.length > 0 ? value.includes(notificationType) : false;
    });
export const selectShowFCCForNotificationTypes = (
    notificationType: ConfigConstants.NotificationTypes,
): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyShowFCCForNotificationTypes];
        return value && notificationType.length > 0 ? value.includes(notificationType) : false;
    });
export const selectShowBootOtherStockWarning = (taskGroup: string): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyShowBootOtherStockWarning];
        return value && taskGroup.length > 0 ? value.includes(taskGroup) : true;
    });
export const selectCriticalityApplicableOnNotifType = (
    notificationType: ConfigConstants.NotificationTypes,
): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyCriticalityNotifTypes];
        return value && notificationType.length > 0 ? value.includes(notificationType) : false;
    });
export const selectRadioactiveSurveyDivisions = (division: string): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyRadioactiveSurveyDivisions];
        return value && division.length > 0 ? value.includes(division) : false;
    });
export const selectRadioactiveSurveyRegions = (region: string): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyRadioactiveSurveyRegions];
        return value && region && region.length > 0 ? value.includes(region) : false;
    });
export const selectMaterialLiBatteryStandAlone = (unNumber: string): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyMaterialLiBatteryStandAlone];
        return value && unNumber.length > 0 ? value.split(', ').some((entry) => entry === unNumber) : false;
    });
export const selectMaterialLiBatteryInDevice = (unNumber: string): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyMaterialLiBatteryInDevice];
        return value && unNumber.length > 0 ? value.split(', ').some((entry) => entry === unNumber) : false;
    });
export const selectMaterialOrderPriorityForSplit = (materialOrderPriority: string): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyMaterialOrderPriorityForSplit];
        return value && materialOrderPriority?.length > 0
            ? value.split(', ').some((entry) => entry === materialOrderPriority)
            : false;
    });
export const selectHideComplaintsForUserStatusType = (
    userStatusType: ConfigConstants.USER_STATUS,
): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const userStatusTypeArray = userStatusType.split(/,\s*|\s+/);
        const value = entries[ConfigConstants.configKeyHideComplaintsForUserStatusType];
        return value && userStatusTypeArray.length > 0
            ? userStatusTypeArray.some((userStatus) => userStatus.length > 0 && value.includes(userStatus))
            : false;
    });
export const selectHideRecoveryTimeForNotificationTypes = (
    notificationType: ConfigConstants.NotificationTypes,
    notificationCategory: string[],
): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyHideRecoveryTimeForNotificationTypes];
        const notificationTypeAndCategoryArray = value && value.length > 0 ? value.split(',') : [];
        return notificationTypeAndCategoryArray.some((item) => {
            const trimmedValue = item.trim();
            const two = 2;
            if (trimmedValue.length === two) {
                return notificationType === trimmedValue;
            }
            const notificationTypeAndCategory = trimmedValue.split('-');
            return (
                notificationTypeAndCategory[0] === notificationType &&
                notificationCategory.includes(notificationTypeAndCategory[1])
            );
        });
    });
export const selectParticipantCopySupportByNotificationType = (
    notificationType: ConfigConstants.NotificationTypes,
): MemoizedSelector<object, string[]> =>
    createSelector(selectConfigEntries, (entries) => {
        const formTypes = entries[ConfigConstants.configKeyPaceFormsTypesForParticipantCopy];
        if (!formTypes) {
            return [];
        }
        try {
            const support = JSON.parse(formTypes) as ConfigParticipantCopySupportByNotificationType;
            return support[notificationType] ?? [];
        } catch {
            return [];
        }
    });

export const selectDateTimeFormatPattern = makeStringConfigSelector(
    ConfigConstants.configKeyDateTimeFormatPattern,
    ConfigConstants.formatPatternDateTimeGeneric,
);
export const selectUseCsmlUrlWithParam = makeBooleanConfigSelector(ConfigConstants.configKeyUseCsmlUrlWithParam, true);

export const selectMaterialOrderNonSplitUnits = (unit: string): MemoizedSelector<object, boolean> =>
    createSelector(selectConfigEntries, (entries) => {
        const value = entries[ConfigConstants.configKeyMaterialOrderNonSplitUnits];
        return value && unit?.length > 0
            ? value.split(', ').some((entry) => entry.toLowerCase() === unit.toLowerCase())
            : false;
    });

export const selectEnableMatOrderCheckOnlineOnly = makeBooleanConfigSelector(
    ConfigConstants.configKeyEnableMatOrderCheckOnlineOnly,
    false,
);
